import React, {useEffect, useState} from 'react';
import {Stack, TextField, Typography} from "@mui/material"
import {TemplateStandardParts} from "../../api/json/TemplateJson"
import {AnyColumnJson} from "../../api/json/ColumnJson"
import TemplateUtil from "../../library/TemplateUtil"
import TemplateRenderer from "./TemplateRenderer"
import ColumnReferenceTextField from "./ColumnReferenceTextField"
import {TemplateType} from "../../api/enum/TemplateType"
import ClassifierOutputSelect from "./ClassifierOutputSelect"

interface TemplateEditorStandardProps {
    columns: AnyColumnJson[]
    templateParts: TemplateStandardParts
    readonly?: boolean
    classifierOutput?: boolean
    onChange: (properties: TemplateStandardParts) => void
}

function TemplateEditorStandard({columns, templateParts, readonly, classifierOutput, onChange}: TemplateEditorStandardProps) {
    const [prompt, setPrompt] = useState<string>(templateParts.prompt)
    const [completion, setCompletion] = useState<string>(templateParts.completion)
    const [activeSection, setActiveSection] = useState<"prompt" | "completion" | null>(null)

    const [separator, setSeparator] = useState<string>(templateParts.separator)
    const [stopSequence, setStopSequence] = useState<string>(templateParts.stopSequence)

    const currentProperties: TemplateStandardParts = {
        type: TemplateType.STANDARD,
        prompt: prompt,
        completion: completion,
        separator: separator,
        stopSequence: stopSequence,
    }

    useEffect(() => {
        onChange(currentProperties)
    }, [prompt, completion, separator, stopSequence])

    const usedReferences = TemplateUtil.findAllReferences(currentProperties)
    const unreferencedFields = columns.filter(f => usedReferences.indexOf(f.reference) === -1)

    if (readonly) {
        return (
            <TemplateRenderer templateParts={templateParts}
                              showLabels
                              staticSystemPromptTreatment="show"
            />
        )
    }

    return (
        <>
            <Stack spacing={2}>
                <ColumnReferenceTextField
                    label="Input"
                    value={prompt}
                    setValue={setPrompt}
                    unreferencedFields={unreferencedFields}
                    showInsertButtons={activeSection === "prompt"}
                    didFocus={() => setActiveSection("prompt")}
                />

                <TextField
                    label="Separator"
                    helperText="Separates each input from its output."
                    value={separator}
                    multiline
                    onChange={e => setSeparator(e.target.value)}
                />

                {classifierOutput
                    ? <ClassifierOutputSelect columns={columns} value={completion} setValue={setCompletion}/>
                    : <ColumnReferenceTextField
                        label="Output"
                        value={completion}
                        setValue={setCompletion}
                        unreferencedFields={unreferencedFields}
                        showInsertButtons={activeSection === "completion"}
                        didFocus={() => setActiveSection("completion")}
                    />
                }

                <TextField
                    label="Stop Sequence"
                    helperText="Signal to stop generating."
                    value={stopSequence}
                    multiline
                    onChange={e => setStopSequence(e.target.value)}
                />
            </Stack>
        </>
    );
}

export default TemplateEditorStandard;